import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { Checkbox, Modal, notification } from 'antd'
import { CustomButton } from 'components'
import { paymentMethods } from 'apiv2'
import { getSafeReturnUrl } from 'utils/sessionSharing'
import { clearReturnTo, loadReturnTo } from './returnTo'
import { MSG_CARD_IN_USE, MSG_RETRY_FAILED_CHECK, deleteWarning, retryResultText } from './messages'
import './index.styl'

const { confirm } = Modal

const errorText = error => error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Something went wrong. Please try again.'

const brandName = brand => (brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card')
const expiry = card => `${String(card.expMonth).padStart(2, '0')}/${String(card.expYear).slice(-2)}`

const RETRY_AFTER_401_MS = 700
const PERSIST_WAIT_MS = 2000
const RETURN_DELAY_MS = 1500 // the "card saved" toast stays visible before going back to Sou-Sou

// The v2 axios wrapper builds the Authorization header from the persisted session at call time and throws synchronously
// when nothing is persisted: run every API call through here so that becomes a rejected promise.
const run = fn => {
  try {
    return Promise.resolve(fn())
  } catch (error) {
    return Promise.reject(error)
  }
}

// Same source the axios wrapper reads (redux-persist writes it asynchronously after the store changed)
const persistedToken = () => {
  try {
    return JSON.parse(JSON.parse(localStorage.getItem('persist:blueline-ticket')).session).token
  } catch (error) {
    return null
  }
}

const PaymentMethods = () => {
  const token = useSelector(state => state.session?.token)
  const retryTimer = useRef(null)
  const stripe = useStripe()
  const elements = useElements()
  const [cards, setCards] = useState(null)
  const [loadError, setLoadError] = useState('')
  const [adding, setAdding] = useState(false)
  const [makeDefault, setMakeDefault] = useState(true)
  const [busy, setBusy] = useState('')
  const [retryLines, setRetryLines] = useState([])
  // where to go back to after a card was added (validated Sou-Sou URL), see ./returnTo
  const [returnTo] = useState(() => loadReturnTo(window.location.search))
  const [returning, setReturning] = useState(false)
  const [offerReturn, setOfferReturn] = useState(false)
  const returnTimer = useRef(null)

  // Right after sign-in the persisted session may not hold the token yet (the request would go out with "Bearer null"):
  // never call the API without a token, and retry once after a 401.
  const load = useCallback((retry = true) => {
    setLoadError('')
    return run(() => paymentMethods.list())
      .then(({ data }) => {
        const items = data?.items || []
        setCards(items)
        return items
      })
      .catch(error => {
        if (retry && error?.response?.status === 401) {
          // the timer is cleared on unmount (a pending promise is simply dropped)
          return new Promise(resolve => { retryTimer.current = setTimeout(resolve, RETRY_AFTER_401_MS) }).then(() => load(false))
        }
        setLoadError(errorText(error))
        setCards([])
        return null
      })
  }, [])

  useEffect(() => () => {
    clearTimeout(retryTimer.current)
    clearTimeout(returnTimer.current)
    clearReturnTo() // leaving the page inside the app ends the Sou-Sou round trip
  }, [])

  // Any other action during the toast window ends the pending return to Sou-Sou
  const cancelReturn = () => {
    clearTimeout(returnTimer.current)
    setReturning(false)
  }

  // Validated again right before navigating (the state value came from the URL or from storage)
  const goBackToSouSou = () => {
    const url = getSafeReturnUrl(returnTo)
    if (!url) return
    clearReturnTo()
    window.location.assign(url)
  }

  // Wait until the persisted session holds the token the store already has (right after sign-in the wrapper would
  // otherwise send the previous/empty token and get a 401); give up waiting after 2 s and let the single retry handle it
  useEffect(() => {
    if (!token) return undefined
    let cancelled = false
    let timer
    const started = Date.now()
    const tick = () => {
      if (cancelled) return
      if (persistedToken() === token || Date.now() - started > PERSIST_WAIT_MS) {
        load()
      } else {
        timer = setTimeout(tick, 50)
      }
    }
    tick()
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [token, load])

  // Shows what the backend reports about the one-time retry of outstanding hands after a card change
  const showRetryResult = result => {
    if (result?.outstandingRetryFailed) {
      setRetryLines([MSG_RETRY_FAILED_CHECK])
    } else if (result?.outstandingRetry?.triggered) {
      setRetryLines(result.outstandingRetry.results.map(retryResultText))
    } else {
      setRetryLines([])
    }
  }

  const onAddCard = async () => {
    if (!stripe || !elements || busy) return
    setBusy('add')
    setRetryLines([])
    try {
      const { data: intent } = await run(() => paymentMethods.createSetupIntent())
      const { error, setupIntent } = await stripe.confirmCardSetup(intent.clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      })
      if (error) {
        notification.warning({ message: error.message })
        return
      }
      const { data: result } = await run(() => paymentMethods.register(setupIntent?.id || intent.setupIntentId, makeDefault))
      notification.success({ message: 'Your card was saved.' })
      showRetryResult(result)
      setAdding(false)
      const items = await load()
      // Back to Sou-Sou only after the add fully succeeded: card confirmed, registered and listed
      const newId = result?.paymentMethod?.paymentMethodId
      const listed = !!items && (!newId || items.some(card => card.paymentMethodId === newId))
      if (returnTo && listed) {
        // outstanding payments that need the user (retry check failed, a payment declined or needing bank action):
        // keep the lines on the page, the user returns with the button
        const needsAttention = result?.outstandingRetryFailed ||
          (result?.outstandingRetry?.results || []).some(line => line.status === 'FAILED' || line.status === 'REQUIRES_ACTION')
        if (needsAttention) {
          setOfferReturn(true)
        } else {
          setReturning(true)
          returnTimer.current = setTimeout(goBackToSouSou, RETURN_DELAY_MS)
        }
      }
    } catch (error) {
      notification.warning({ message: errorText(error) })
    } finally {
      setBusy('')
    }
  }

  const onMakeDefault = card => {
    if (busy) return
    cancelReturn()
    setBusy(card.paymentMethodId)
    setRetryLines([])
    run(() => paymentMethods.setDefault(card.paymentMethodId))
      .then(({ data }) => {
        notification.success({ message: 'Your default card was changed.' })
        showRetryResult(data)
        return load()
      })
      .catch(error => notification.warning({ message: errorText(error) }))
      .finally(() => setBusy(''))
  }

  const doRemove = card =>
    run(() => paymentMethods.remove(card.paymentMethodId))
      .then(() => {
        notification.success({ message: 'The card was removed.' })
        setRetryLines([])
        return load()
      })
      .catch(error => {
        const code = error?.response?.data?.code
        notification.warning({ message: code === 'CARD_IN_USE_BY_SUBSCRIPTION' ? MSG_CARD_IN_USE : errorText(error) })
      })

  const onRemove = card => {
    if (busy) return
    cancelReturn()
    const warning = deleteWarning(card.activeCirclesAffected)
    confirm({
      title: `Remove ${brandName(card.brand)} •••• ${card.last4}?`,
      content: warning || undefined,
      okText: 'Remove',
      cancelText: 'Cancel',
      onOk: () => {
        setBusy(card.paymentMethodId)
        return doRemove(card).finally(() => setBusy(''))
      },
    })
  }

  return (
    <div className='sectionProfile wrapperPaymentMethods'>
      <div className='head'>
        <div className='titleSection'>Payment Methods</div>
        {!adding && <CustomButton text='Add card' type='white' onClick={() => { cancelReturn(); setAdding(true) }} />}
      </div>
      <div className='note'>
        Your saved cards are shared across TicketLyfe memberships and Sou-Sou. The default card is used for new payments.
      </div>

      {loadError && <div className='error'>{loadError}</div>}
      {cards === null && <div className='empty'>Loading…</div>}
      {cards && !loadError && cards.length === 0 && !adding && <div className='empty'>You have no saved cards yet.</div>}

      {cards && cards.map(card => (
        <div className='cardRow' key={card.paymentMethodId}>
          <div className='cardInfo'>
            <div className='cardName'>
              {brandName(card.brand)} •••• {card.last4}
              {card.isDefault && <span className='badge'>Default</span>}
              {card.isExpired && <span className='badge expired'>Expired</span>}
            </div>
            <div className='cardExp'>Expires {expiry(card)}</div>
          </div>
          <div className='cardActions'>
            {!card.isDefault && (
              <CustomButton text='Make default' type='white' loading={busy === card.paymentMethodId} onClick={() => onMakeDefault(card)} />
            )}
            <CustomButton text='Remove' type='white' disabled={!!busy} onClick={() => onRemove(card)} />
          </div>
        </div>
      ))}

      {adding && (
        <div className='addCard'>
          <div className='cardField'><CardElement options={{ hidePostalCode: true }} /></div>
          <Checkbox checked={makeDefault} onChange={e => setMakeDefault(e.target.checked)}>Make this my default card</Checkbox>
          <div className='addActions'>
            <CustomButton text='Cancel' type='white' disabled={busy === 'add'} onClick={() => setAdding(false)} />
            <CustomButton text='Save card' type='dark' loading={busy === 'add'} disabled={!stripe} onClick={onAddCard} />
          </div>
        </div>
      )}

      {returning && <div className='returning'>Returning to Sou-Sou…</div>}
      {offerReturn && (
        <div className='returning'>
          <CustomButton text='Return to Sou-Sou' type='dark' onClick={goBackToSouSou} />
        </div>
      )}

      {retryLines.length > 0 && (
        <div className='retryResult'>
          {retryLines.map(line => <div key={line}>{line}</div>)}
        </div>
      )}
    </div>
  )
}

export default PaymentMethods
